import Accordion from '@repo/ui/accordion'
import Button from '@repo/ui/button'
import CapsuleTab, { Tab } from '@repo/ui/capsule-tab'
import {
  ArrowLeftRight,
  ClockIcon,
  GoalIcon,
  MealIcon,
  PdfIcon,
  PlusIcon,
  PrintIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'

import { getTripFolder } from '../../../api/trip-folder/get-trip-folder-details'
import TripFolderAddMission from '../../../components/pages/trip-folders/trip-folder-add-mission'
import TripFolderCentersSection from '../../../components/pages/trip-folders/trip-folder-centers-section'
import TripFolderCreateTransfer from '../../../components/pages/trip-folders/trip-folder-create-transfer'
import TripFolderDetailsSection, {
  formatTripFolderDetails
} from '../../../components/pages/trip-folders/trip-folder-details-section'
import { TripFolderGeneratePdf } from '../../../components/pages/trip-folders/trip-folder-generate-pdf'
import TripFolderInstructionsSection from '../../../components/pages/trip-folders/trip-folder-intrusctions-section'
import TripFolderMissionSection from '../../../components/pages/trip-folders/trip-folder-missions-section'
import TripFolderOlArrivalsSection from '../../../components/pages/trip-folders/trip-folder-ol-arrivals-section'
import TripFolderOrderMealForm from '../../../components/pages/trip-folders/trip-folder-order-meal-form'
import TripFolderReservationsSection from '../../../components/pages/trip-folders/trip-folder-reservation/trip-folder-reservations-section'
import TripFolderSecondaryTripSection from '../../../components/pages/trip-folders/trip-folder-secondary-trip/trip-folder-secondary-trip-section'
import TripFolderStats from '../../../components/pages/trip-folders/trip-folder-stats'
import TripFolderTransfersSection from '../../../components/pages/trip-folders/trip-folder-transfers-section'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { useScrollSections } from '../../../lib/hooks/use-scroll-sections'
import { TripFolderTypeEnum } from '../../../lib/types/enums/trip-folder/trip-folder-type.enum'
import { TransferPlace } from '../../../lib/types/models/transfer/transfer-place'

type FolderDetailsProps = {
  folderId: number
}

type AccordionDetail = {
  component: React.ReactNode
  subtitle?: React.ReactNode
}

type Key =
  | 'details'
  | 'reservation'
  | 'transfers'
  | 'missions'
  | 'centers'
  | 'secondary-trip'
  | 'ol-arrivals'
  | 'instructions'

export type TripFolderTimezone = 'Europe/Paris' | 'Local'

const timezoneTabs: Tab<'required-key', TripFolderTimezone>[] = [
  { label: 'Heure française', key: 'Europe/Paris', icon: ClockIcon },
  { label: 'Heure locale', key: 'Local' }
]

const FolderDetails = ({ folderId }: FolderDetailsProps) => {
  const [activeTimezone, setActiveTimezone] = useState<TripFolderTimezone>('Europe/Paris')
  const [selectedTransfers, setSelectedTransfers] = useState<TransferPlace[]>([])

  const { data: tripFolder } = useQuery({
    queryKey: ['trip-folder', folderId],
    queryFn: () => getTripFolder(folderId),
    select: formatTripFolderDetails,
    enabled: !!folderId
  })

  const tabs = useMemo<Tab<'required-key', Key>[]>(
    () => [
      { label: 'Détails', key: 'details' },
      { label: `Réservations`, key: 'reservation' },
      { label: 'Missions Bureau LEC/AAT', key: 'missions' },
      {
        label: 'Encadrants & centres',
        key: 'centers',
        disabled:
          tripFolder?.type === TripFolderTypeEnum.SECONDARY_ONE_WAY ||
          tripFolder?.type === TripFolderTypeEnum.SECONDARY_RETURN
      },
      {
        label: 'Transferts',
        key: 'transfers',
        disabled: false
      },
      {
        label: 'Voyages secondaires',
        key: 'secondary-trip',
        disabled:
          tripFolder?.type &&
          ![TripFolderTypeEnum.MAIN_ONE_WAY, TripFolderTypeEnum.MAIN_RETURN].includes(
            tripFolder.type
          )
      },
      {
        label: "Arrivée à l'étranger",
        key: 'ol-arrivals',
        disabled: false
      }
    ],
    [tripFolder?.type]
  )

  // meals are delivered to the return transfers that take the youths to their secondary trips
  const canOrderMeal =
    !!tripFolder?.type &&
    [TripFolderTypeEnum.MAIN_RETURN, TripFolderTypeEnum.SECONDARY_RETURN].includes(tripFolder.type)

  const [accordionState, setAccordionState] = useState<Key[]>(tabs.map((tab) => tab.key))

  const { active, handleChange } = useScrollSections({ sections: tabs })

  const { modalState, toggleModal } = useModalManager<
    'add-mission' | 'create-transfer' | 'order-meal' | 'generate-pdf',
    string
  >(['add-mission', 'create-transfer', 'order-meal', 'generate-pdf'])

  const renderAccordion = (key: Key): AccordionDetail => {
    switch (key) {
      case 'details':
        return {
          component: (
            <TripFolderDetailsSection folderId={folderId} activeTimezone={activeTimezone} />
          )
        }
      case 'reservation':
        return {
          component: (
            <TripFolderReservationsSection folderId={folderId} activeTimezone={activeTimezone} />
          )
        }
      case 'missions':
        return {
          component: <TripFolderMissionSection folderId={folderId} />,
          subtitle: (
            <Button
              variant="secondary"
              label="Ajouter une mission"
              icon={PlusIcon}
              onClick={(e) => {
                e.stopPropagation()
                toggleModal('add-mission')
              }}
            />
          )
        }
      case 'centers':
        return {
          component: <TripFolderCentersSection folderId={folderId} />
        }
      case 'transfers':
        return {
          component: (
            <TripFolderTransfersSection
              folderId={folderId}
              tripFolderType={tripFolder?.type}
              selectedTransfers={selectedTransfers}
              onSelectedTransfersChange={setSelectedTransfers}
            />
          ),
          subtitle: (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                label="Créer un transfert"
                icon={PlusIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('create-transfer')
                }}
              />
              {canOrderMeal && (
                <Button
                  variant="secondary"
                  label="Commander les repas"
                  disabled={selectedTransfers.length === 0}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleModal('order-meal')
                  }}
                />
              )}
            </div>
          )
        }
      case 'secondary-trip':
        return {
          component: <TripFolderSecondaryTripSection folderId={folderId} />
        }
      case 'ol-arrivals':
        return {
          component: (
            <TripFolderOlArrivalsSection folderId={folderId} activeTimezone={activeTimezone} />
          )
        }
      default:
        return {
          component: <div>Content not available</div>
        }
    }
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="sticky top-20 z-10 bg-white pb-3">
        <CapsuleTab
          outline
          hideDisabled
          variant="secondary"
          activeTab={active}
          onTabChange={handleChange}
          tabs={tabs}
        />
      </div>

      <Fragment>
        <div className="flex items-center justify-between w-full">
          <TripFolderStats folderId={folderId} />

          <Button
            variant="ghost"
            label="Imprimer"
            icon={PdfIcon}
            onClick={() => toggleModal('generate-pdf')}
          />
        </div>

        <div className="w-fit self-end">
          <CapsuleTab
            outline
            size="small"
            variant="tertiary"
            activeTab={timezoneTabs.indexOf(
              timezoneTabs.find((tab) => tab.key === activeTimezone) ?? timezoneTabs[0]
            )}
            onTabChange={(index) => {
              setActiveTimezone(timezoneTabs[index].key)
            }}
            tabs={timezoneTabs}
          />
        </div>

        {tabs.map(
          (tab) =>
            !tab.disabled && (
              <Accordion
                key={tab.key}
                variant="primary"
                id={tab.key}
                title={tab.label}
                subtitle={renderAccordion(tab.key).subtitle}
                open={accordionState.includes(tab.key)}
                onToggle={() => {
                  if (accordionState.includes(tab.key))
                    setAccordionState(accordionState.filter((key) => key !== tab.key))
                  else setAccordionState([...accordionState, tab.key])
                }}
              >
                {renderAccordion(tab.key).component}
              </Accordion>
            )
        )}

        <TripFolderInstructionsSection folderId={folderId} />
        <div className="h-4" />
      </Fragment>

      <Modal
        isOpen={modalState['add-mission']}
        title={'Ajouter une mission'}
        icon={GoalIcon}
        size="large"
        hideActions
        onCancel={() => toggleModal('add-mission')}
      >
        <TripFolderAddMission
          tripFolderId={folderId}
          toggleModal={() => toggleModal('add-mission')}
        />
      </Modal>

      <Modal
        isOpen={modalState['create-transfer']}
        title={'Créer un transfert'}
        icon={ArrowLeftRight}
        size="medium"
        hideActions
        onCancel={() => toggleModal('create-transfer')}
      >
        <TripFolderCreateTransfer
          toggleModal={() => toggleModal('create-transfer')}
          className={MODAL_BODY_CLASSNAME}
        />
      </Modal>

      <Modal
        isOpen={modalState['generate-pdf']}
        title={'Imprimer'}
        icon={PrintIcon}
        size={'medium'}
        hideActions
        onCancel={() => toggleModal('generate-pdf')}
      >
        <TripFolderGeneratePdf
          tripFolderId={folderId}
          toggleModal={() => toggleModal('generate-pdf')}
        />
      </Modal>

      {canOrderMeal && (
        <Modal
          isOpen={modalState['order-meal']}
          title={'Commander les repas'}
          icon={MealIcon}
          size="medium"
          hideActions
          onCancel={() => toggleModal('order-meal')}
        >
          <TripFolderOrderMealForm
            toggleModal={() => toggleModal('order-meal')}
            folderId={folderId}
            transfers={selectedTransfers}
          />
        </Modal>
      )}
    </div>
  )
}

export default FolderDetails
